import React from 'react';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';
import { AlertCircle, Clock, Mail, Phone, FileText, CheckCircle, ArrowRight, Scale } from 'lucide-react';

export default function ComplaintsProcedure() {
  return (
    <PublicLayout>
      <SEO 
        title="Customer Complaints Procedure | LetWise"
        description="How to make a complaint to LetWise. Our complaints procedure ensures fair and timely resolution in accordance with Property Redress Scheme requirements."
      />
      
      <div className="bg-slate-50 min-h-screen">
        {/* Header */}
        <div className="bg-primary text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <AlertCircle className="w-16 h-16 mx-auto mb-6 opacity-80" />
              <h1 className="text-4xl font-bold mb-4">Customer Complaints Procedure</h1>
              <p className="text-xl opacity-90">
                C&C LET MANAGEMENT LIMITED trading as LetWise
              </p>
              <p className="text-sm mt-4 opacity-75">
                PRS Member: PRS056298
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            
            {/* Introduction */}
            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Commitment</h2>
              <p className="text-slate-600 mb-4">
                We are committed to providing a high standard of service. However, if something goes 
                wrong, we want to hear about it so we can put it right. This procedure explains how 
                to make a complaint and what you can expect from us.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-blue-800">
                  <strong>Important:</strong> We aim to resolve all complaints within 8 weeks. If we 
                  cannot resolve your complaint to your satisfaction, you may refer it to the Property 
                  Redress Scheme for independent review.
                </p>
              </div>
            </div>

            {/* Step-by-Step Process */}
            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">How to Make a Complaint</h2>
              
              {/* Step 1 */}
              <div className="flex gap-4 mb-8">
                <div className="flex-shrink-0 w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Contact Us First</h3>
                  <p className="text-slate-600 mb-4">
                    Many issues can be resolved quickly by speaking directly to your property manager 
                    or our customer service team. Contact us by:
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-slate-50 p-4 rounded-lg flex items-start gap-3">
                      <Mail className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-900">Email</p>
                        <a href="mailto:complaints@letwise.io" className="text-primary hover:underline text-sm">
                          complaints@letwise.io
                        </a>
                      </div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg flex items-start gap-3">
                      <Phone className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-900">Phone</p>
                        <p className="text-sm text-slate-600">Mon-Fri 9am-5pm</p>
                      </div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg flex items-start gap-3">
                      <FileText className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-900">In Writing</p>
                        <p className="text-sm text-slate-600">Post to our office</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4 mb-8">
                <div className="flex-shrink-0 w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Formal Complaint</h3>
                  <p className="text-slate-600 mb-4">
                    If you're not satisfied with our initial response, or you'd prefer to make a formal 
                    complaint from the outset, please put your complaint in writing to:
                  </p>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="font-medium text-slate-900">Complaints Manager</p>
                    <p className="text-slate-600">C&C LET MANAGEMENT LIMITED</p>
                    <p className="text-slate-600">Unit 4, 416 Hamilton Road</p>
                    <p className="text-slate-600">Cambuslang, Glasgow</p>
                    <p className="text-slate-600">Scotland, G72 7XR</p>
                    <p className="text-slate-600 mt-2">Email: <a href="mailto:complaints@letwise.io" className="text-primary hover:underline">complaints@letwise.io</a></p>
                  </div>
                  <div className="mt-4">
                    <p className="text-slate-600 font-medium mb-2">Please include:</p>
                    <ul className="list-disc pl-6 space-y-1 text-slate-600">
                      <li>Your name and contact details</li>
                      <li>The property address (if applicable)</li>
                      <li>A clear description of your complaint</li>
                      <li>What you would like us to do to resolve it</li>
                      <li>Copies of any relevant documents</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4 mb-8">
                <div className="flex-shrink-0 w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Our Response Timeline</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-900">Within 3 working days</p>
                        <p className="text-slate-600">We will acknowledge your complaint in writing</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-900">Within 15 working days</p>
                        <p className="text-slate-600">We aim to provide a full response. If we need more time, we will let you know</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-900">Within 8 weeks maximum</p>
                        <p className="text-slate-600">We will provide our final response (as required by PRS)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg">
                  4
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">If You're Still Not Satisfied</h3>
                  <p className="text-slate-600 mb-4">
                    If you remain dissatisfied after receiving our final response, or if 8 weeks have 
                    passed, you may refer your complaint to the Property Redress Scheme for free, 
                    independent review.
                  </p>
                </div>
              </div>
            </div>

            {/* Property Redress Scheme */}
            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Scale className="w-6 h-6 text-primary" />
                Property Redress Scheme (PRS)
              </h2>
              <p className="text-slate-600 mb-6">
                We are members of the Property Redress Scheme, an independent body approved by the 
                government to handle complaints about property agents. The PRS can consider your 
                complaint free of charge.
              </p>
              <div className="bg-slate-50 rounded-lg p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="font-medium text-slate-900 mb-2">Contact Details:</p>
                    <p className="text-slate-600">Property Redress Scheme</p>
                    <p className="text-slate-600">Premiere House</p>
                    <p className="text-slate-600">1st Floor</p>
                    <p className="text-slate-600">Elstree Way, Borehamwood</p>
                    <p className="text-slate-600">Hertfordshire, WD6 1JH</p>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 mb-2">How to Contact:</p>
                    <p className="text-slate-600">
                      Website: <a href="https://www.theprs.co.uk" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">www.theprs.co.uk</a>
                    </p>
                    <p className="text-slate-600">Email: info@theprs.co.uk</p>
                    <p className="text-slate-600">Phone: 0333 321 9418</p>
                    <p className="text-slate-600 mt-4 font-medium">Our Membership: PRS056298</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800">
                  <strong>Note:</strong> You must refer your complaint to the PRS within 12 months of 
                  receiving our final response. The PRS cannot consider complaints about matters that 
                  occurred more than 12 months before you first complained to us.
                </p>
              </div>
            </div>

            {/* Scotland Specific */}
            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Scotland - Additional Information</h2>
              <p className="text-slate-600 mb-4">
                As our registered office is in Scotland, Scottish law applies to our operations. 
                Landlords letting property in Scotland should also be aware of:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600">
                <li>
                  <strong>Scottish Landlord Registration:</strong> All landlords must be registered 
                  with their local authority
                </li>
                <li>
                  <strong>First-tier Tribunal (Housing and Property Chamber):</strong> For disputes 
                  about tenancy deposits or tenancy terms
                </li>
                <li>
                  <strong>Letting Agent Code of Practice:</strong> We comply with the Scottish 
                  Government's Code of Practice for Letting Agents
                </li>
              </ul>
            </div>

            {/* What We Can Help With */}
            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">What We Can Consider</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    We Can Help With:
                  </h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      Quality of our service
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      Staff conduct and communication
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      Fees and charges
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      Marketing and advertising issues
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      Management of your property/tenancy
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                      Deposit handling
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Outside Our Scope:
                  </h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-red-600 mt-1 flex-shrink-0" />
                      Disputes between landlord and tenant
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-red-600 mt-1 flex-shrink-0" />
                      Legal matters requiring court action
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-red-600 mt-1 flex-shrink-0" />
                      Matters already being considered by a court
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-red-600 mt-1 flex-shrink-0" />
                      Events that occurred before you became our customer
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Card */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-primary" />
                Company Information
              </h2>
              <div className="text-slate-600 space-y-2">
                <p><strong>C&C LET MANAGEMENT LIMITED</strong> trading as <strong>LetWise</strong></p>
                <p>Unit 4, 416 Hamilton Road, Cambuslang, Glasgow, Scotland, G72 7XR</p>
                <p>Company Number: SC869470</p>
                <p>PRS Membership: PRS056298</p>
                <p className="mt-4">
                  Complaints Email: <a href="mailto:complaints@letwise.io" className="text-primary hover:underline">complaints@letwise.io</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
